import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanInput } from '@/components';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { type PasswordFormData, passwordSchema } from '@/features/settings/validations/password';
import { usePassword } from '../../queries/usePassword';

export const PasswordSection: React.FC = () => {
    const { user } = useUser();
    const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
    const { mutateAsync: updatePasswordMutation, isPending: isSubmitting } = usePassword();

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        watch,
    } = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
        defaultValues: {
            current_password: '',
            password: '',
            password_confirmation: '',
        },
    });

    const watchedValues = watch();

    const isButtonDisabled = isSubmitting || !isDirty;

    const onSubmit = async (data: PasswordFormData) => {
        setFormErrors({});

        try {
            await updatePasswordMutation(data);

            // Analytics
            stanAnalytics('password-update', {
                meta: {
                    user_id: user?.user_id,
                    username: user?.username,
                },
                props: {
                    update_type: 'password_change',
                },
            });

            stanNotify({
                type: 'success',
                title: 'Password Updated!',
                text: 'Your password has been updated successfully.',
            });

            // Reset form
            reset();
        } catch (error: any) {
            setFormErrors(error.response?.data?.errors || {});
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message || 'Please try again later.',
            });
            console.error('Password update error:', error);
        }
    };

    return (
        <section className='w-full max-w-3xl space-y-6'>
            <h4>Password</h4>

            <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <div>
                        <StanInput
                            {...register('current_password')}
                            name='current_password'
                            value={watchedValues.current_password || ''}
                            type='password'
                            topLabel='Current Password'
                            error={!!errors.current_password || !!formErrors.current_password}
                            hintText={errors.current_password?.message || formErrors.current_password?.[0]}
                        />
                    </div>
                </div>

                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <div>
                        <StanInput
                            {...register('password')}
                            name='password'
                            value={watchedValues.password || ''}
                            type='password'
                            topLabel='New Password'
                            error={!!errors.password || !!formErrors.password}
                            hintText={errors.password?.message || formErrors.password?.[0]}
                        />
                    </div>

                    <div>
                        <StanInput
                            {...register('password_confirmation')}
                            name='password_confirmation'
                            value={watchedValues.password_confirmation || ''}
                            type='password'
                            topLabel='Confirm Password'
                            error={!!errors.password_confirmation || !!formErrors.password_confirmation}
                            hintText={errors.password_confirmation?.message || formErrors.password_confirmation?.[0]}
                        />
                    </div>
                </div>

                <div className='flex items-center gap-4'>
                    <StanButton
                        name='password-update-button'
                        type='submit'
                        disabled={isButtonDisabled}
                        loading={isSubmitting}
                    >
                        Update
                    </StanButton>

                    {isSubmitting && (
                        <div className='text-caption-1-regular flex items-center'>
                            <div className='mr-2 size-4 animate-spin rounded-full border-b-2' />
                            Updating...
                        </div>
                    )}
                </div>
            </form>
        </section>
    );
};
